import { useState, useEffect } from 'react';
import {
  Ambulance, FileText, Users, Receipt, MapPin, Sparkles,
  Inbox, BarChart3, CheckCircle2, Calendar, Shield, Clock,
  ArrowRight, X, Menu, CalendarCheck, LayoutGrid,
} from 'lucide-react';
import { toast } from '../components/UI';
import BrandLogo from '../components/BrandLogo';

// Colores de marca (independientes del tema): azul del logo + azul marino.
const BRAND_PRIMARY = '#2557D6';
const BRAND_NAVY = '#0B2545';
const BRAND_GRADIENT = 'linear-gradient(135deg, #2F6BEA, #1E46B4)';

const EVENT_IMAGES = [
  { src: '/brand/event-sports.webp', label: 'Eventos deportivos' },
  { src: '/brand/event-stadium.webp', label: 'Estadios y campos' },
  { src: '/brand/event-festival.webp', label: 'Conciertos y festivales' },
  { src: '/brand/event-cycling.webp', label: 'Ciclismo y carreras' },
  { src: '/brand/event-ambulance.webp', label: 'Ambulancias modernas' },
  { src: '/brand/event-rally.webp', label: 'Rallys y motor' },
];

const FEATURES = [
  { Icon: Ambulance,    title: 'Gestión de flota',        desc: 'Control total de ambulancias SVA, SVB, TSNU. Asignación inteligente por disponibilidad, tipo y nivel preventivo.', img: '/brand/feature-fleet.webp' },
  { Icon: FileText,     title: 'Presupuestos en 1 clic',  desc: 'Genera presupuestos PDF profesionales con tarifas por cliente, firma digital y seguimiento en tiempo real.',       img: '/brand/feature-quotes.webp'  },
  { Icon: Users,        title: 'Portal de profesionales', desc: 'Médicos, enfermeros y TES gestionan su disponibilidad, aceptan servicios y ven liquidaciones desde el móvil.',     img: '/brand/feature-pros.webp'  },
  { Icon: Receipt,      title: 'Facturación completa',    desc: 'Facturas automáticas, anticipos, pagos parciales y liquidaciones por profesional. Todo trazado.',                 img: '/brand/feature-billing.webp'  },
  { Icon: MapPin,       title: 'Geolocalización',         desc: 'Mapa con cada punto de cobertura. Para eventos grandes, cada recurso puede ubicarse en un lugar diferente.',       img: '/brand/feature-geo.webp'  },
  { Icon: Sparkles,     title: 'Auto-asignación IA',      desc: 'Asignación automática cruzando categoría profesional, nivel preventivo, disponibilidad y equilibrio de horas.',    img: '/brand/feature-ai.webp'  },
];

// PLANS hardcoded REMOVED — ahora se cargan dinámicamente desde
// GET /api/auth/public-plans en el componente LandingPage. SA puede modificar
// precios, features y planes desde el panel y se reflejan en landing al instante
// (cache 60s).

const HERO_TRUST = [
  { Icon: Shield,    text: 'Datos cifrados y aislados por empresa' },
  { Icon: Users,     text: 'Flota y profesionales en un solo panel' },
  { Icon: BarChart3, text: 'Coordinación en tiempo real' },
];

const STEPS = [
  { step: '01', Icon: Inbox,        title: 'Tu cliente solicita',       desc: 'Accede al portal, indica evento, fecha, ubicación y recursos. Recibes la solicitud al instante.' },
  { step: '02', Icon: BarChart3,    title: 'Presupuestas y planificas', desc: 'Ves disponibilidad real de vehículos y personal. Generas un PDF con tarifas personalizadas.' },
  { step: '03', Icon: CheckCircle2, title: 'Asignas y cubres',          desc: 'Asigna vehículos y tripulación. La IA equilibra horas entre profesionales automáticamente.' },
];

const TRUST = [
  { Icon: Shield,   title: 'Datos seguros',       desc: 'Cifrado AES-256-GCM para PII, tenant isolation estricto, backups diarios.' },
  { Icon: Calendar, title: 'Siempre disponible',  desc: 'Infraestructura redundante. Tu panel funciona el día del evento, sí o sí.' },
  { Icon: Clock,    title: 'Onboarding rápido',   desc: 'Configura tu empresa y empieza a cotizar el mismo día. Sin integraciones largas.' },
];

const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', outline: 'none', background: 'var(--surface-alt)', color: 'var(--text)' };

export default function LandingPage({ onGoToApp, onGoToSignup }) {
  const [showDemo, setShowDemo] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [plans, setPlans] = useState(null); // null = cargando, [] = error/vacío
  const [plansError, setPlansError] = useState(false);

  // Carga planes dinámicamente desde el backend SA — SA puede modificar precios
  // y aparecen al instante en la landing (cache 60s).
  useEffect(() => {
    fetch('/api/auth/public-plans')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setPlans(d.plans || []))
      .catch(() => { setPlans([]); setPlansError(true); });
  }, []);

  // Fallbacks if parent didn't pass navigation handlers (e.g. rendered standalone)
  const goLogin  = onGoToApp    || (() => { window.location.href = '/login'; });
  const goSignup = onGoToSignup || (() => { window.location.href = '/login?view=signup_tenant'; });

  return (
    <div style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", color: 'var(--text)', background: 'var(--bg)', overflowX: 'hidden' }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&family=Space+Mono:wght@700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes lpFadeIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        .lp-hero{display:grid;grid-template-columns:minmax(0,0.95fr) minmax(0,1.25fr);align-items:center;gap:40px}
        .lp-hero-media{position:relative;border-radius:28px;overflow:hidden;aspect-ratio:1156/724;-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 14%);mask-image:linear-gradient(90deg,transparent 0,#000 14%)}
        .lp-hero-media img{width:100%;height:100%;object-fit:cover;display:block}
        .lp-hero-trust{display:flex;flex-wrap:wrap;gap:12px 28px}
        .lp-nav{color:var(--text);background:color-mix(in srgb, var(--surface) 88%, transparent);border-bottom:1px solid var(--border)}
        .lp-nav .lp-link{color:var(--text-muted)}
        @media (max-width: 900px){
          .lp-hero{grid-template-columns:1fr}
          .lp-hero-media{-webkit-mask-image:none;mask-image:none;border-radius:20px}
        }
        @media (prefers-reduced-motion: reduce){ .lp-fade{animation:none !important} }
        .lp-feat-card{transition:all 0.3s ease}
        .lp-feat-card:hover{transform:translateY(-6px);box-shadow:0 20px 50px rgba(0,0,0,0.12)}
        .lp-feat-card:hover .lp-feat-img{transform:scale(1.05)}
        .lp-plan-card{transition:all 0.25s}
        .lp-plan-card:hover{transform:translateY(-4px);box-shadow:0 16px 40px rgba(0,0,0,0.1)}
        .lp-link{color:var(--text-muted);text-decoration:none;font-size:14px;font-weight:500;transition:color 0.2s}
        .lp-link:hover{color:${BRAND_PRIMARY}}
        /* Footer keeps a dark brand tint in both themes */
        .lp-nav-dark{color:#fff}
        .lp-nav-dark .lp-link{color:#cbd5e1}
        .lp-nav-dark .lp-link:hover{color:${BRAND_PRIMARY}}
        @media (max-width: 720px) {
          .lp-nav-links{display:none !important}
          .lp-nav-mobile{display:flex !important}
        }
        @media (min-width: 721px) {
          .lp-nav-mobile{display:none !important}
          .lp-mobile-sheet{display:none !important}
        }
      `}</style>

      {/* ═══ NAV ═══ (glass claro/oscuro según tema, como el resto de la web) */}
      <nav className="lp-nav" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backdropFilter: 'blur(20px)' }}>
        <a href="/" aria-label="MedCover Pro — inicio" style={{ textDecoration: 'none' }}>
          <BrandLogo size={34} />
        </a>

        <div className="lp-nav-links" style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <a href="#features" className="lp-link">Funciones</a>
          <a href="#how" className="lp-link">Cómo funciona</a>
          <a href="#pricing" className="lp-link">Precios</a>
          <button onClick={goLogin} className="lp-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Entrar</button>
          <button onClick={goSignup} style={{ padding: '9px 18px', background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 14px rgba(37,87,214,0.3)' }}>
            Empezar gratis <ArrowRight size={15} />
          </button>
        </div>

        <button className="lp-nav-mobile" onClick={() => setMobileMenu(m => !m)} aria-label={mobileMenu ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={mobileMenu} style={{ display: 'none', background: 'var(--surface-alt)', border: '1px solid var(--border)', color: 'var(--text)', padding: 8, borderRadius: 8, cursor: 'pointer', alignItems: 'center' }}>
          {mobileMenu ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {mobileMenu && (
        <div className="lp-mobile-sheet lp-nav" style={{ position: 'fixed', top: 64, left: 0, right: 0, zIndex: 99, backdropFilter: 'blur(20px)', padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <a href="#features" onClick={() => setMobileMenu(false)} className="lp-link" style={{ fontSize: 16 }}>Funciones</a>
          <a href="#how" onClick={() => setMobileMenu(false)} className="lp-link" style={{ fontSize: 16 }}>Cómo funciona</a>
          <a href="#pricing" onClick={() => setMobileMenu(false)} className="lp-link" style={{ fontSize: 16 }}>Precios</a>
          <button onClick={goLogin} style={{ padding: '12px 18px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Entrar</button>
          <button onClick={goSignup} style={{ padding: '12px 18px', background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Empezar prueba gratis</button>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      <section style={{ position: 'relative', overflow: 'hidden', background: 'radial-gradient(ellipse at 85% 30%, color-mix(in srgb, var(--accent) 14%, transparent), transparent 60%), var(--bg)' }}>
        <div className="lp-hero" style={{ padding: '112px 24px 64px', maxWidth: 1280, margin: '0 auto' }}>
          <div className="lp-fade" style={{ animation: 'lpFadeIn 0.7s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
              <span style={{ width: 36, height: 3, borderRadius: 2, background: BRAND_PRIMARY }} />
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Eventos seguros, personas tranquilas</span>
            </div>
            <h1 style={{ fontSize: 'clamp(38px, 5.2vw, 68px)', fontWeight: 900, lineHeight: 1.04, margin: '0 0 22px', letterSpacing: -2, color: 'var(--text)' }}>
              Cobertura médica para eventos,{' '}
              <span style={{ color: BRAND_PRIMARY }}>gestionada con precisión.</span>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 1.6vw, 20px)', color: 'var(--text-muted)', maxWidth: 560, lineHeight: 1.6, margin: '0 0 34px' }}>
              La plataforma para empresas de ambulancias: solicitudes, presupuestos, flota, profesionales y facturación de eventos deportivos, conciertos y grandes afluencias en un solo lugar.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 40 }}>
              <button onClick={goSignup} style={{ padding: '16px 28px', background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 30px rgba(37,87,214,0.3)', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <CalendarCheck size={19} /> Empezar prueba gratis <ArrowRight size={18} />
              </button>
              <button onClick={() => setShowDemo(true)} style={{ padding: '16px 28px', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <LayoutGrid size={18} /> Solicitar demo
              </button>
            </div>
            <div className="lp-hero-trust">
              {HERO_TRUST.map(({ Icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-muted)', maxWidth: 190, lineHeight: 1.35 }}>
                  <Icon size={24} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                  {text}
                </div>
              ))}
            </div>
          </div>

          <div className="lp-hero-media lp-fade" style={{ animation: 'lpFadeIn 0.9s ease' }}>
            <img src="/brand/hero-event.webp" alt="Equipo sanitario de MedCover Pro coordinando la cobertura de una carrera junto a una ambulancia" width={1156} height={724} fetchpriority="high" />
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section style={{ padding: '48px 24px', background: 'var(--surface)', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
        <p style={{ fontSize: 12, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600, marginBottom: 20 }}>Diseñado para empresas de ambulancias reales</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap', opacity: 0.85 }}>
          {['Coberturas deportivas', 'Festivales y ferias', 'Carreras populares', 'Circuitos de velocidad', 'Eventos corporativos'].map((t, i) => (
            <span key={i} style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>{t}</span>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" style={{ padding: '96px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 900, color: 'var(--text)', letterSpacing: -1.5, margin: '0 0 12px' }}>Todo lo que necesitas para operar</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 17, maxWidth: 600, margin: '0 auto' }}>Una plataforma completa que cubre cada etapa del proceso de cobertura sanitaria</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          {FEATURES.map((f, i) => {
            const Icon = f.Icon;
            return (
              <div key={i} className="lp-feat-card" style={{ background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ height: 170, overflow: 'hidden', position: 'relative' }}>
                  <img className="lp-feat-img" src={f.img} alt={f.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }} loading="lazy" />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.55) 0%, transparent 60%)' }} />
                  <div style={{ position: 'absolute', bottom: 12, left: 14, width: 40, height: 40, borderRadius: 10, background: 'rgba(37,87,214,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
                    <Icon size={20} strokeWidth={2.2} />
                  </div>
                </div>
                <div style={{ padding: '20px 24px 24px' }}>
                  <h3 style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ EVENT TYPES GALLERY ═══ */}
      <section style={{ padding: '72px 24px', background: 'var(--surface-alt)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: 'var(--text)', letterSpacing: -1.5, margin: '0 0 12px' }}>Cubrimos todo tipo de eventos</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 16 }}>Deportivos, culturales, musicales, motor... donde haya personas, hay que estar preparados</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {EVENT_IMAGES.map((img, i) => (
              <div key={i} className="lp-feat-card" style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', aspectRatio: '16/10' }}>
                <img src={img.src} alt={img.label} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s' }} className="lp-feat-img" />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '36px 16px 14px', background: 'linear-gradient(to top, rgba(0,0,0,0.72), transparent)', color: '#fff', fontWeight: 700, fontSize: 15 }}>
                  {img.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AMBULANCE SHOWCASE ═══ (brand teal — intentional, theme-agnostic) */}
      <section style={{ padding: '80px 24px', background: BRAND_NAVY, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'url(/brand/event-ambulance.webp) center/cover', opacity: 0.15 }} />
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: '#fff', letterSpacing: -1.5, margin: '0 0 16px' }}>Flota moderna, tecnología avanzada</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', maxWidth: 620, margin: '0 auto 40px', lineHeight: 1.6 }}>
            Gestiona ambulancias tipo A1, A2, B y C. Checklists de equipamiento por tipo, asignación de bases y seguimiento GPS en tiempo real.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16, maxWidth: 600, margin: '0 auto' }}>
            {[
              { t: 'A1', sub: 'TSNU' },
              { t: 'A2', sub: 'TSNU+' },
              { t: 'B',  sub: 'SVB' },
              { t: 'C',  sub: 'SVA/SVAE' },
            ].map(({ t, sub }) => (
              <div key={t} style={{ padding: '16px 0', background: 'rgba(255,255,255,0.12)', borderRadius: 12, backdropFilter: 'blur(10px)' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', fontFamily: "'Space Mono', monospace" }}>{t}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how" style={{ padding: '96px 24px', background: 'var(--surface)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,87,214,0.1) 0%, transparent 70%)' }} />
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: 'var(--text)', letterSpacing: -1.5, margin: '0 0 12px' }}>Así de fácil</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 16, marginBottom: 48 }}>De la solicitud al servicio cubierto en tres pasos</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {STEPS.map(({ step, Icon, title, desc }, i) => (
              <div key={i} style={{ textAlign: 'left', padding: 28, background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(37,87,214,0.12)', color: BRAND_PRIMARY, fontSize: 18, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Mono', monospace" }}>{step}</div>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(37,87,214,0.08)', color: BRAND_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={20} strokeWidth={2} />
                  </div>
                </div>
                <h3 style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>{title}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TRUST SIGNALS ═══ */}
      <section style={{ padding: '72px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 28 }}>
          {TRUST.map(({ Icon, title, desc }, i) => (
            <div key={i} style={{ padding: 28, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, textAlign: 'left' }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(37,87,214,0.1)', color: BRAND_NAVY, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Icon size={22} strokeWidth={2} />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>{title}</h3>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ PRICING ═══ — DINÁMICO desde /api/auth/public-plans */}
      <section id="pricing" style={{ padding: '96px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, color: 'var(--text)', letterSpacing: -1.5, margin: '0 0 12px' }}>Planes sin sorpresas</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 16 }}>
            {plans && plans.some(p => p.has_trial)
              ? `Prueba ${Math.max(...plans.map(p => p.trial_days || 0))} días gratis. `
              : ''}
            Suscripción anual prorrateada en 12 cuotas. Cancela en cualquier momento (sigues pagando hasta vencimiento).
          </p>
        </div>
        {plans === null ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Cargando planes…</div>
        ) : plans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            {plansError ? 'No se pudieron cargar los planes. Reintenta en unos segundos.' : 'Próximamente.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {plans.map((p) => {
              const isPopular = p.highlight && /vendido|recomendado|popular/i.test(p.highlight);
              const monthly = p.billing_cycle === 'yearly' ? (p.price / 12).toFixed(0) : p.price;
              const accentColor = p.color || BRAND_PRIMARY;
              return (
                <div key={p.id} className="lp-plan-card" style={{
                  padding: 32, position: 'relative',
                  background: isPopular ? '#0f172a' : 'var(--card)',
                  color: isPopular ? '#fff' : 'var(--text)',
                  borderRadius: 20,
                  border: isPopular ? `2px solid ${accentColor}` : '1px solid var(--border)',
                }}>
                  {p.highlight && (
                    <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', padding: '5px 20px', background: BRAND_GRADIENT, color: '#fff', borderRadius: 12, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 12px rgba(37,87,214,0.3)', whiteSpace: 'nowrap' }}>
                      {p.highlight}
                    </div>
                  )}
                  <h3 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>{p.name}</h3>
                  {p.description && <p style={{ fontSize: 12, color: isPopular ? '#94a3b8' : 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>{p.description}</p>}
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 44, fontWeight: 900, fontFamily: "'Space Mono', monospace" }}>€{monthly}</span>
                    <span style={{ fontSize: 14, color: isPopular ? '#94a3b8' : 'var(--text-muted)' }}>/mes</span>
                  </div>
                  <div style={{ fontSize: 11, color: isPopular ? '#94a3b8' : 'var(--text-dim)', marginBottom: 14 }}>
                    {p.billing_cycle === 'yearly' ? `Facturado €${p.price}/año (12 cuotas)` : 'Facturado mensualmente'}
                    {p.has_trial && ` · ${p.trial_days} días gratis`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, fontSize: 14, color: isPopular ? '#cbd5e1' : 'var(--text-muted)' }}>
                    <CheckCircle2 size={16} style={{ color: accentColor, flexShrink: 0, marginTop: 2 }} />
                    <span><strong>{p.users_label}</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, fontSize: 14, color: isPopular ? '#cbd5e1' : 'var(--text-muted)' }}>
                    <CheckCircle2 size={16} style={{ color: accentColor, flexShrink: 0, marginTop: 2 }} />
                    <span>{p.modules_count} módulos incluidos</span>
                  </div>
                  {p.modules.slice(0, 6).map((m) => (
                    <div key={m.slug} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8, fontSize: 13, color: isPopular ? '#cbd5e1' : 'var(--text-muted)' }}>
                      <CheckCircle2 size={14} style={{ color: accentColor, flexShrink: 0, marginTop: 3, opacity: 0.7 }} />
                      <span>{m.name}</span>
                    </div>
                  ))}
                  {p.modules.length > 6 && (
                    <div style={{ fontSize: 12, color: isPopular ? '#94a3b8' : 'var(--text-dim)', fontStyle: 'italic', marginBottom: 8 }}>
                      …y {p.modules.length - 6} módulos más
                    </div>
                  )}
                  {p.commitment_months > 0 && (
                    <div style={{ fontSize: 11, color: isPopular ? '#94a3b8' : 'var(--text-dim)', padding: 8, background: isPopular ? 'rgba(255,255,255,0.05)' : 'var(--surface-alt)', borderRadius: 6, marginTop: 12, marginBottom: 4 }}>
                      ⓘ Compromiso {p.commitment_months} meses. {p.commitment_text}
                    </div>
                  )}
                  <button onClick={goSignup} style={{ width: '100%', marginTop: 14, padding: '13px 0', background: isPopular ? BRAND_GRADIENT : BRAND_NAVY, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                    {p.has_trial ? `Empezar prueba gratis (${p.trial_days}d)` : 'Contratar ahora'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ═══ CTA ═══ (brand gradient — intentional, theme-agnostic) */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #0B2545, #1E46B4)', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: '#fff', margin: '0 0 14px' }}>¿Listo para digitalizar tu empresa?</h2>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, marginBottom: 28 }}>Empieza con 14 días gratis. Sin tarjeta de crédito. Sin permanencia.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={goSignup} style={{ padding: '16px 36px', background: '#fff', color: BRAND_NAVY, border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Empezar prueba gratis <ArrowRight size={18} />
          </button>
          <button onClick={() => setShowDemo(true)} style={{ padding: '16px 36px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(6px)' }}>
            Solicitar demo
          </button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ (brand-dark — intentional, theme-agnostic) */}
      <footer className="lp-nav-dark" style={{ padding: '40px 24px 32px', background: '#0b1220', borderTop: '1px solid #1e293b' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 28, marginBottom: 28 }}>
          <div>
            <div style={{ marginBottom: 14 }}>
              <BrandLogo size={32} tone="light" showTagline />
            </div>
            <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, margin: 0 }}>Plataforma SaaS para coberturas sanitarias y gestión de ambulancias.</p>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Producto</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a href="#features" className="lp-link">Funciones</a>
              <a href="#pricing" className="lp-link">Precios</a>
              <a href="#how" className="lp-link">Cómo funciona</a>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Empresa</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a href="mailto:hola@thmedcoverpro.com" className="lp-link">Contacto</a>
              <a href="#" className="lp-link">Soporte</a>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Legal</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a href="#" className="lp-link">Términos</a>
              <a href="#" className="lp-link">Privacidad</a>
              <a href="#" className="lp-link">Cookies</a>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '0 auto', paddingTop: 20, borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>© 2026 MedCover Pro. THMallorca.</p>
          <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>Hecho en Mallorca</p>
        </div>
      </footer>

      {/* ═══ DEMO MODAL ═══ */}
      {showDemo && (
        <div onClick={() => setShowDemo(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', color: 'var(--text)', borderRadius: 20, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.25)', animation: 'lpFadeIn 0.3s ease', position: 'relative' }}>
            <button onClick={() => setShowDemo(false)} aria-label="Cerrar" style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, display: 'inline-flex' }}>
              <X size={20} />
            </button>
            <h3 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 6px', color: 'var(--text)' }}>Solicitar demo</h3>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 22px' }}>Déjanos tus datos y te contactamos en menos de 24h. O empieza ya <button onClick={goSignup} style={{ background: 'none', border: 'none', color: BRAND_NAVY, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 14, textDecoration: 'underline' }}>con la prueba gratis</button>.</p>
            <input placeholder="Nombre de tu empresa *" style={inputStyle} />
            <input placeholder="Tu nombre *" style={inputStyle} />
            <input placeholder="Email *" type="email" style={inputStyle} />
            <input placeholder="Teléfono" type="tel" style={inputStyle} />
            <textarea placeholder="¿Cuántos vehículos y profesionales tienes?" rows="2" style={{ ...inputStyle, resize: 'vertical' }} />
            <button onClick={() => { toast.success('Listo', '¡Gracias! Te contactaremos pronto.'); setShowDemo(false); }} style={{ width: '100%', marginTop: 4, padding: '13px 0', background: BRAND_GRADIENT, color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,87,214,0.3)' }}>
              Solicitar demo
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', marginTop: 12, marginBottom: 0 }}>Sin compromiso. Sin tarjeta de crédito.</p>
          </div>
        </div>
      )}
    </div>
  );
}
